import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  FormControlLabel,
  Typography,
  CircularProgress,
  Skeleton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from '../../../context/SnackbarContext';
import { listHubspotCompanies } from '../../../api/connector/hubspot';
import type { HubspotCompany } from '../../../types/hubspot';

interface HubSpotCompanyPickerProps {
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  disabled?: boolean;
}

/**
 * Company list picker for scheduled import "company_list" scope.
 * Reuses the same list/selection UX as the manual import (ImportHubspotList).
 */
const HubSpotCompanyPicker: React.FC<HubSpotCompanyPickerProps> = ({
  selectedIds,
  onSelectionChange,
  disabled = false
}) => {
  const { showSnackbar } = useSnackbar();
  const [items, setItems] = useState<HubspotCompany[]>([]);
  const [offsetId, setOffsetId] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterByOwner, setFilterByOwner] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const selectedSet = new Set(selectedIds);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setLoadingList(true);
    listHubspotCompanies({
      limit: 10,
      offsetId: undefined,
      search: debouncedSearch.trim() || undefined,
      filter_by_my_owner: filterByOwner
    })
      .then((res) => {
        setItems(res.records ?? []);
        setOffsetId(res.offsetId ?? null);
      })
      .catch((e: any) => {
        showSnackbar(e?.message || 'Failed to load companies', 'error');
        setItems([]);
        setOffsetId(null);
      })
      .finally(() => setLoadingList(false));
  }, [debouncedSearch, filterByOwner, showSnackbar]);

  const loadMore = () => {
    if (!offsetId) return;
    setLoadingMore(true);
    listHubspotCompanies({
      limit: 10,
      offsetId,
      search: debouncedSearch.trim() || undefined,
      filter_by_my_owner: filterByOwner
    })
      .then((res) => {
        setItems((prev) => [...prev, ...(res.records ?? [])]);
        setOffsetId(res.offsetId ?? null);
      })
      .catch((e: any) => {
        showSnackbar(e?.message || 'Failed to load more', 'error');
      })
      .finally(() => setLoadingMore(false));
  };

  const handleToggle = (id: string) => {
    if (selectedSet.has(id)) {
      onSelectionChange(selectedIds.filter((x) => x !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const handleSelectAllVisible = () => {
    const visibleIds = items.map((c) => c.id);
    const combined = new Set([...selectedIds, ...visibleIds]);
    onSelectionChange(Array.from(combined));
  };

  const handleDeselectAllVisible = () => {
    const visibleSet = new Set(items.map((c) => c.id));
    onSelectionChange(selectedIds.filter((id) => !visibleSet.has(id)));
  };

  const visibleSelectedCount = items.filter((c) => selectedSet.has(c.id)).length;
  const isAllVisibleSelected = items.length > 0 && visibleSelectedCount === items.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 640 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        Select companies
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={filterByOwner}
              onChange={(e) => setFilterByOwner(e.target.checked)}
              size="small"
              disabled={disabled || loadingList}
            />
          }
          label="Only show my companies"
        />
        <TextField
          size="small"
          placeholder="Search companies..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={disabled || loadingList}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            onClick={isAllVisibleSelected ? handleDeselectAllVisible : handleSelectAllVisible}
            disabled={disabled || loadingList || items.length === 0}
            sx={{ textTransform: 'capitalize' }}
          >
            {isAllVisibleSelected ? 'Deselect all' : 'Select all'}
          </Button>
          {selectedIds.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {selectedIds.length} selected
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {loadingList ? (
          <List dense sx={{ p: 0 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Box key={i} px={2} py={1.25}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="40%" />
                {i < 5 && <Divider sx={{ mt: 1 }} />}
              </Box>
            ))}
          </List>
        ) : (
          <List dense sx={{ p: 0 }}>
            {items.map((company, idx) => (
              <React.Fragment key={company.id}>
                <ListItemButton
                  disabled={disabled}
                  onClick={() => handleToggle(company.id)}
                  dense
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={selectedSet.has(company.id)}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={company.properties?.name || 'Unnamed Company'}
                    secondary={company.properties?.domain || company.properties?.website || 'No domain'}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
                {idx < items.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
      {offsetId && !loadingList && (
        <Button
          size="small"
          onClick={loadMore}
          disabled={disabled || loadingMore}
          sx={{ alignSelf: 'center', textTransform: 'capitalize' }}
        >
          {loadingMore ? <CircularProgress size={18} /> : 'Load more'}
        </Button>
      )}
    </Box>
  );
};

export default HubSpotCompanyPicker;
